import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import devMode from './dev-mode';
import { lenisSetup, lenisExceptions } from './partials/lenis';
import { lottieInit } from './partials/lottie-init';
import columnBreak from './utils/column-break';
import parallax from './utils/parallax';
import { linksCustomization, scrollTo, responsiveTables, factCircle, countUp } from './partials/misc';
import scrollAnimations from './vendor/scrollAnimations-v2';
import animations from './utils/animations';
import popups from './partials/popups'
import buttons from './partials/buttons';
import { initSFTranslate } from './partials/search-filter-translate';
import { video } from './partials/video';
import { wpGallery } from './partials/galleries';
import { lazyImageFade } from './partials/lazy-image-fade';
import { loadIframes } from './partials/iframes';
import { followingCursor } from './partials/following-cursor';
import error404Animation from './partials/404';
import eventSingleStickySidebar from './partials/event-single';
import { sliders } from './partials/sliders';
import { laPasserelle } from './partials/la-passerelle';
import notifications from '../../templates/sections/notifications/notifications';
import pillarsAnimation from '../../templates/blocks/pillars-block/pillars-block';
import accordion from '../../templates/blocks/accordion';
import accordionWithSlider from '../../templates/blocks/accordion-with-slider';
import tabs from '../../templates/blocks/tabs/tabs';
import textAndMedia from '../../templates/blocks/text-and-media/text-and-media';
import timeline from '../../templates/blocks/timeline/timeline';
import blockquote from '../../templates/blocks/blockquote/blockquote';
import ctasAndLogos from '../../templates/blocks/ctas-and-logos/ctas-and-logos';

import menus from './partials/menus';
import { bannerSkipLinks, bannerStrategicPlan } from './partials/banner';
import { bannerVideo } from '../../templates/sections/banner/banner-video';

gsap.registerPlugin(ScrollTrigger);


function consoleMessage(){
	window.console.log('%cCreated by %cTWK%cwww.thewebkitchen.co.uk', 'background: #13212E; color: #FFFFFF; padding: 5px 0px 5px 10px; margin: 25px 0px;', 'background: #13212E; color: #05E5C8; padding: 5px 10px 5px 0px; font-weight: bold;', 'background: #FFFFFF; padding: 5px 10px;');
}

function usingJS(){
	document.querySelector('html').classList.remove('no-js')
	document.querySelector('html').classList.add('js')
}

/**
 * Run an init function so that one failing module can't stop the others.
 */
function safeInit(name, fn) {
	try {
		fn();
	} catch (error) {
		// window.console on purpose: production builds strip plain console.* calls, and these errors must stay visible.
		window.console.error(`[twk] ${name} failed`, error);
	}
}

function dataHiddenOnLoad() {
	document.querySelectorAll( '[data-hidden-on-load]' )?.forEach(function(el){
		el.removeAttribute('data-hidden-on-load')
	})
}

function dataHiddenUntilScroll() {
	const elements = document.querySelectorAll('[data-hidden-until-scroll]');
	if (elements.length === 0) return;

	const threshold = 5;
	let revealed = false;

	const onScroll = () => {
		if (revealed) return;
		const scrollPos = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
		if (scrollPos > threshold) {
			revealed = true;
			elements.forEach(el => el.removeAttribute('data-hidden-until-scroll'));
			window.removeEventListener('scroll', onScroll, { passive: true });
		}
	};

	window.addEventListener('scroll', onScroll, { passive: true });
}



/* *********************** */
/* Start all the functions */
/* *********************** */


/* *********************** */
/* 1. RUN IMMEDIATELY      */
/* *********************** */
safeInit('consoleMessage', consoleMessage)
safeInit('usingJS', usingJS)
safeInit('initSFTranslate', initSFTranslate)

/* ******************************* */
/* 2. CRITICAL — INITIALISE        */
/* Safe because this script is     */
/* deferred (DOM is parsed)        */
/* ******************************* */

// Reveal-related inits first: if anything below fails, content must still show.
safeInit('dataHiddenOnLoad', dataHiddenOnLoad)
safeInit('dataHiddenUntilScroll', dataHiddenUntilScroll)
safeInit('columnBreak', columnBreak) // Layout change, so before the scroll animations measure positions.
safeInit('scrollAnimations', scrollAnimations)

// Init basic utilities
safeInit('devMode', devMode)
safeInit('bannerSkipLinks', bannerSkipLinks)
safeInit('linksCustomization', linksCustomization)

// Setup smooth scroll — must be early so page feels responsive
let lenis = null;
safeInit('lenisSetup', () => { lenis = lenisSetup(); })
if (lenis) {
	safeInit('lenisExceptions', () => lenisExceptions(lenis))
	safeInit('scrollTo', () => scrollTo(lenis))
}

safeInit('bannerStrategicPlan', bannerStrategicPlan)

// Navigation — critical for all pages
safeInit('menus', menus)
safeInit('buttons', buttons)
safeInit('bannerVideo', bannerVideo)

// Popups — needs to be ready before user interaction
safeInit('popups', popups)

// Store lenis on window so the onload phase can access it
window._lenis = lenis;


/* ******************************* */
/* 3. DEFERRED — window.onload     */
/* After all resources (images/iframes) loaded */
/* ******************************* */
const initDeferred = () => {
	// Retrieve lenis initialised in DOMContentLoaded
	const lenis = window._lenis;

	// Non-critical utilities
	safeInit('parallax', parallax)
	safeInit('lottieInit', lottieInit)
	safeInit('responsiveTables', responsiveTables)

	if (document.querySelector('[data-name="video-lazy-load"]')) {
		safeInit('video', video);
	}

	if (document.querySelector('.gallery[class*="gallery-columns-"]')) {
		safeInit('wpGallery', wpGallery);
	}

	if (document.querySelector('img[data-lazy-fade="true"]')) {
		safeInit('lazyImageFade', lazyImageFade);
	}

	if (document.querySelector('iframe[data-src]')) {
		safeInit('loadIframes', loadIframes);
	}

	// Initialize animations (this creates ScrollTriggers)
	safeInit('animations', animations)

	if (document.querySelector('[data-name="video"]')) {
		safeInit('followingCursor', followingCursor);
	}

	if (document.querySelector('[data-name="notification-popup"]')) {
		safeInit('notifications', notifications);
	}

	if (document.querySelector('#error-404-svg')) {
		safeInit('error404Animation', error404Animation);
	}

	if (document.querySelector('[data-event-sidebar]')) {
		safeInit('eventSingleStickySidebar', eventSingleStickySidebar);
	}

	if (document.querySelector('[data-block="pillars-block"]')) {
		safeInit('pillarsAnimation', pillarsAnimation);
	}

	const initSliders = () => {
		const sliderSelectors = [
			'[data-name$="-slider"]',
			'[data-name="overflow-slider"]',
			'.f-carousel'
		];
		const hasSliders = sliderSelectors.some(sel => document.querySelector(sel));

		if (hasSliders) {
			safeInit('sliders', () => {
				sliders();
				requestAnimationFrame(() => {
					ScrollTrigger.sort();
					ScrollTrigger.refresh();
				});
			});
		}
	};

	setTimeout(initSliders, 10);

	if (document.querySelector('[data-target="accordion"]')) {
		safeInit('accordion', accordion);
	}

	if (document.querySelector('[data-name="accordion-with-slider"]')) {
		safeInit('accordionWithSlider', accordionWithSlider);
	}

	if (document.querySelector('[data-name="tabs-container"]')) {
		safeInit('tabs', tabs);
	}

	if (document.querySelector('[data-name="iframe-video"]')) {
		safeInit('textAndMedia', textAndMedia);
	}

	if (document.querySelector('[data-name="timeline"]')) {
		safeInit('timeline', timeline);
	}

	safeInit('factCircle', factCircle)
	safeInit('countUp', countUp)

	if (document.querySelector('[data-name="no-image"]')) {
		safeInit('blockquote', blockquote);
	}

	if (document.body.classList.contains('page-template-la-passerelle')) {
		safeInit('laPasserelle', () => laPasserelle(lenis));
	}

	if (document.querySelector('[data-block="ctas-and-logos"]')) {
		safeInit('ctasAndLogos', ctasAndLogos);
	}

	if (document.fonts) {
		document.fonts.ready.then(() => {
			ScrollTrigger.refresh();
		});
	}

	// Final catch-all refresh after layout settles
	setTimeout(() => {
		ScrollTrigger.refresh();
	}, 500);
};

if (document.readyState === 'complete') {
	initDeferred();
} else {
	window.addEventListener('load', initDeferred, { once: true });
}
